/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { Footer } from '@components/systems'
import { mono, monoForm, monoShared } from '@components/systems/monolith/monolith.tokens'

import { useNavigate } from 'react-router-dom'
import {
	useAppSelector,
	useAppDispatch,
	PaymentStateType,
	paymentActions,
} from '@redux/index'
import { formatAmount } from '@utils'
import {
	apiOrderStatus,
	apiCardIntegratedResult,
	apiCancelCardOrder,
	readKrwPending,
	clearKrwPending,
	clearStoredCart,
} from '@redux/saga/home/payment.saga'
import { CartType, PaymentDeliveryType, PaymentCustomerInfoType } from '@redux/index'

/**
 * 결제 완료 — Monolith(2안). 구 페이지는 `pages/home/PaymentSuccess.page.tsx` 에 보관.
 *
 * 구 페이지는 100px 짜리 🎉 이모지와 30rem 고정 폭 행이었다 (DESIGN_SYSTEM §5 이모지 금지).
 * 여기서는 주문서를 스펙 표(§5)로 읽힌다 — 확인하러 온 화면이라 읽을 것이 값뿐이다.
 * redux(payment.paymentSuccess) 값은 그대로 쓴다.
 */
const MonolithPaymentSuccessPage = () => {
	// hook
	const navigate = useNavigate()
	const dispatch = useAppDispatch()

	// redux state
	const successMessage: PaymentStateType['paymentSuccess'] = useAppSelector(
		state => state.payment.paymentSuccess,
	)
	const tryToPayment: PaymentStateType['tryToPayment'] = useAppSelector(
		state => state.payment.tryToPayment,
	)
	const paymentDelivery: PaymentDeliveryType = useAppSelector(
		state => state.payment.paymentDelivery,
	)
	const paymentCustomerInfo: PaymentCustomerInfoType = useAppSelector(
		state => state.payment.paymentCustomerInfo,
	)
	/** 모바일 복귀에서 결제가 안 된 경우의 사유. 비어 있으면 정상 흐름 */
	const [returnFailure, setReturnFailure] = React.useState<string | null>(null)

	/*
		모바일 복귀 (2026-09-20). PayVerse SDK 는 모바일에서 콜백 대신 페이지를 PayVerse 로 보냈다가
		`returnUrl?결과` 로 되돌린다. 결제 사가는 그때 죽었으므로 여기서 이어받는다:
		  ① 탭 저장소의 임시주문 표(orderId · cancelToken)와 URL 의 결과를 합친다
		  ② 결과를 서버 원장에 남긴다(card-integrated-result)
		  ③ SUCCESS 면 "확인 중" 성공 화면(아래 폴링이 확정을 본다) + 카트 비우기
		     실패·취소면 임시주문을 되돌리고 사유를 보여 준다. 카트는 그대로
		     결과 파라미터가 없으면(스펙 불명) 확인 중으로 두고 폴링에 맡긴다
	*/
	React.useEffect(() => {
		const pending = readKrwPending()
		if (pending === null) return

		const params = new URLSearchParams(window.location.search)
		const pick = (...keys: string[]): string => {
			for (const aKey of keys) {
				const value = params.get(aKey)
				if (value !== null && value !== '') return value
			}
			return ''
		}
		const orderId = pick('orderId', 'orderid', 'order_id') || pending.orderId
		if (orderId !== pending.orderId) return

		const resultStatus = pick('resultStatus', 'result_status', 'status')
		const resultCode = pick('resultCode', 'result_code', 'code')
		const resultMessage = pick('resultMessage', 'result_message', 'message')
		const tid = pick('tid')
		const hasResult = resultStatus !== '' || resultCode !== '' || resultMessage !== ''

		clearKrwPending()
		if (window.location.search !== '') {
			window.history.replaceState(null, '', window.location.pathname)
		}

		void apiCardIntegratedResult({
			orderId,
			cancelToken: pending.cancelToken,
			resultStatus: resultStatus || (hasResult ? '' : 'RETURNED'),
			resultCode,
			resultMessage,
			tid,
		})

		const failed = hasResult && resultStatus.toUpperCase() !== 'SUCCESS'
		if (failed) {
			void apiCancelCardOrder(orderId, pending.cancelToken)
			setReturnFailure(
				[resultCode, resultMessage].filter(aPart => aPart !== '').join(' · ') ||
					'The card payment was not completed.',
			)
			return
		}

		const cartList: CartType[] = tryToPayment.cartList
		const orderDetail = cartList
			.map((anItem: CartType) => `${anItem.productName} x ${anItem.qty}`)
			.join(',\n')
		const address =
			paymentDelivery.countryIsoCode === 'KR'
				? `대한민국 ${paymentDelivery.stateName} ${paymentDelivery.city} \n${paymentDelivery.street1} \n${paymentDelivery.street2}`
				: [
						paymentDelivery.street2,
						paymentDelivery.street1,
						paymentDelivery.city,
						paymentDelivery.stateName,
						paymentDelivery.countryName,
				  ].join(' ')

		dispatch(
			paymentActions.PAYMENT_SUCCESS({
				orderId,
				orderDetail,
				address,
				email: pending.customerEmail || paymentCustomerInfo.email,
				phoneNumber: pending.phoneNumber,
				currency:
					(pending.currency as PaymentStateType['paymentSuccess']['currency']) ?? 'KRW',
				paidAmount: Number(pending.requestAmount),
				confirming: true,
				cancelToken: pending.cancelToken,
			}),
		)
		clearStoredCart()
		dispatch(paymentActions.TRIGGER_RESET_PAYMENT_DATA(true))
		// eslint-disable-next-line
	}, [])

	/*
		KRW: 카드창은 성공이라 했지만 서버 확정(웹훅)을 아직 못 봤다. 3초마다 물어서 확정되면 내린다.
		2분 안에 안 오면 그대로 둔다 — 메일이 확정의 증거고, 화면은 "확인 중"을 유지한다.
	*/
	React.useEffect(() => {
		if (!successMessage.confirming || successMessage.orderId === '') return

		let stopped = false
		let attempts = 0
		const timer = window.setInterval(() => {
			void (async () => {
				attempts += 1
				const res = await apiOrderStatus(
					successMessage.orderId,
					successMessage.cancelToken,
				)
				const status = res?.status === 201 ? res.data?.[0] : undefined
				if (stopped) return
				if (status?.paid === true) {
					dispatch(
						paymentActions.PAYMENT_SUCCESS({
							...successMessage,
							currency: status.currency ?? successMessage.currency,
							paidAmount: Number(status.paidAmount ?? successMessage.paidAmount),
							confirming: false,
							cancelToken: '',
						}),
					)
				}
				if (status?.paid === true || attempts >= 40) window.clearInterval(timer)
			})()
		}, 3000)

		return () => {
			stopped = true
			window.clearInterval(timer)
		}
	}, [successMessage.confirming, successMessage.orderId])

	const rowList: { label: string; value: string }[] = [
		{ label: 'Order No.', value: successMessage.orderId },
		{ label: 'Order detail', value: successMessage.orderDetail },
		...(successMessage.paidAmount > 0 && successMessage.currency !== ''
			? [
					{
						label: 'Total paid',
						value: formatAmount(successMessage.currency, successMessage.paidAmount),
					},
			  ]
			: []),
		{ label: 'Address', value: successMessage.address },
		{ label: 'Email', value: successMessage.email },
		{ label: 'Phone', value: successMessage.phoneNumber },
	]

	/**
	 * 주문 없이 주소만 치고 들어온 경우(새로고침·직접 방문). 리드 문장은 주문이 있을 때만 —
	 * 없는데 "확인 메일을 보냈다"고 적으면 사실이 아니다.
	 */
	const hasOrder: boolean = successMessage.orderId !== ''

	if (returnFailure !== null) {
		return (
			<View css={style}>
				<View className="mnWrap">
					<View className="mnDoneHeader">
						<h1 className="mnDoneTitle">Not completed.</h1>
						<p className="mnDoneLede">
							The card payment did not go through. Nothing was charged and the cart is
							unchanged.
						</p>
					</View>
					<View className="mnDoneBody">
						<View className="mnDoneTable">
							<View className="mnSpecRow">
								<span className="mnSpecLabel">Card company response</span>
								<span className="mnSpecValue">{returnFailure}</span>
							</View>
						</View>
						<View className="mnDoneAction">
							<button type="button" className="mnCta" onClick={() => navigate('/cart')}>
								Back to cart
							</button>
						</View>
					</View>
				</View>
				<Footer />
			</View>
		)
	}

	return (
		<View css={style}>
			<View className="mnWrap">
				<View className="mnDoneHeader">
					<h1 className="mnDoneTitle">Thank you.</h1>
					{hasOrder && successMessage.confirming && (
						<p className="mnDoneLede">
							The card payment went through and we are confirming it with the card
							company. This usually takes a few seconds. The confirmation email goes out
							as soon as it is confirmed.
						</p>
					)}
					{hasOrder && !successMessage.confirming && (
						<p className="mnDoneLede">
							The order is placed. We sent a confirmation email with the order details to
							the address below.
						</p>
					)}
				</View>

				<View className="mnDoneBody">
					{hasOrder ? (
						<View className="mnDoneTable">
							{rowList.map(aRow => (
								<View key={aRow.label} className="mnSpecRow">
									<span className="mnSpecLabel">{aRow.label}</span>
									<span className="mnSpecValue">{aRow.value}</span>
								</View>
							))}
						</View>
					) : (
						<p className="mnDoneQuiet">
							This page shows an order right after it is placed.
						</p>
					)}

					<View className="mnDoneAction">
						<button type="button" className="mnCta" onClick={() => navigate('/shop')}>
							Continue shopping
						</button>
						<button
							type="button"
							className="mnTextLink"
							onClick={() => navigate('/customer/order')}
						>
							My orders
						</button>
					</View>
				</View>
			</View>

			<Footer />
		</View>
	)
}

export default MonolithPaymentSuccessPage

const style = css({
	...monoShared,
	...monoForm,
	position: 'relative',
	width: '100%',

	'.mnDoneHeader': {
		padding: 'clamp(56px, 7vw, 100px) 0 clamp(32px, 3.6vw, 48px)',

		'.mnDoneTitle': {
			fontSize: 'clamp(44px, 6vw, 88px)',
			lineHeight: 1,
			fontWeight: 100,
			letterSpacing: '-0.01em',
			color: mono.ink,
		},
		'.mnDoneLede': {
			fontSize: 19,
			lineHeight: 1.6,
			fontWeight: 300,
			color: mono.dim,
			maxWidth: 520,
			marginTop: 'clamp(20px, 2vw, 28px)',
		},
	},

	'.mnDoneBody': {
		width: '100%',
		maxWidth: 760,
		padding: '0 0 clamp(64px, 8.3vw, 120px)',
		borderTop: `1px solid ${mono.line}`,
		paddingTop: 'clamp(40px, 4.4vw, 64px)',
	},

	'.mnDoneQuiet': {
		fontSize: 16,
		fontWeight: 300,
		color: mono.dim,
	},

	'.mnDoneAction': {
		display: 'flex',
		alignItems: 'center',
		gap: 32,
		flexWrap: 'wrap',
		marginTop: 'clamp(40px, 4.4vw, 64px)',
	},
})
